import React, { useState, useEffect, useRef, useContext, useCallback } from 'react';
import { Modal, Form, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch, faPlus, faTrash, faEdit, faFileCsv,
  faCheckCircle, faExclamationTriangle, faSpinner, faSyncAlt
} from '@fortawesome/free-solid-svg-icons';
import API_CONFIG2 from '../../API_CONFIG/API_CONFIG2';
import Dexie from 'dexie';
import { FournisseursContext } from '../layout/CunstomFournisseurs';

// ============================================================
// UTILITAIRE FETCH JSON SÉCURISÉ
// ============================================================
const fetchJSON = async (url, options = {}) => {
  const response = await fetch(url, options);
  const text = await response.text();

  if (!response.ok) {
    throw new Error(`HTTP ${response.status} : ${text || 'Erreur inconnue'}`);
  }

  try {
    return JSON.parse(text);
  } catch (parseError) {
    throw new Error(`Réponse JSON invalide : ${text.substring(0, 200) || '(réponse vide)'}`);
  }
};

// ============================================================
// CACHE DEXIE (24h) avec possibilité de récupérer le cache périmé
// ============================================================
const db = new Dexie('FournisseursCacheDB');
db.version(1).stores({
  fournisseurs: 'key, data, timestamp'
});
const CACHE_DURATION = 24 * 60 * 60 * 1000;

async function getCachedFournisseurs(key, allowExpired = false) {
  try {
    const entry = await db.fournisseurs.get(key);
    if (!entry) return null;
    const age = Date.now() - entry.timestamp;
    if (!allowExpired && age > CACHE_DURATION) return null;
    return entry.data;
  } catch { return null; }
}

async function setCachedFournisseurs(key, data) {
  try {
    await db.fournisseurs.put({ key, data, timestamp: Date.now() });
  } catch { /* ignore */ }
}

async function getStaleFournisseurs(key) {
  return getCachedFournisseurs(key, true);
}

// ============================================================
// DÉDOUBLONNAGE DES REQUÊTES EN COURS (Map globale)
// ============================================================
const pendingFetches = new Map();

const API_URL = `${API_CONFIG2.apiUrl}fournisseur/api_fournisseur.php`;

// ============================================================
// COMPOSANT PRINCIPAL
// ============================================================
const ManageFournisseurs = () => {
  const context = useContext(FournisseursContext) || {};
  const { refreshKey, setSource } = context;

  const [fournisseurs, setFournisseurs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
    limit: 10
  });
  const [dataSource, setDataSource] = useState('cache');

  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [editingFournisseur, setEditingFournisseur] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [formData, setFormData] = useState({
    id: '', nom: '', contact: '', telephone: '', email: '', type_services: ''
  });

  const [toast, setToast] = useState({ show: false, message: '', variant: 'success' });
  const [selectedFile, setSelectedFile] = useState(null);

  const searchTimeoutRef = useRef(null);
  const abortControllerRef = useRef(null);
  const isMounted = useRef(true);

  // ============================================================
  // GESTION DES TOASTS
  // ============================================================
  const showToast = useCallback((message, variant = 'success', duration = 5000) => {
    setToast({ show: true, message, variant });
    setTimeout(() => {
      if (isMounted.current) setToast(prev => ({ ...prev, show: false }));
    }, duration);
  }, []);

  // ============================================================
  // RÉCUPÉRATION DES FOURNISSEURS (avec dédoublonnage + annulation)
  // ============================================================
  const fetchFournisseurs = useCallback(async (page = 1, search = '', forceRefresh = false, retryCount = 0) => {
    // Annuler la requête précédente
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    const cacheKey = `page_${page}_search_${search}`;
    const requestKey = forceRefresh ? `${cacheKey}_force` : cacheKey;

    // Dédoublonnage : si une requête identique est en cours, on l'attend
    if (pendingFetches.has(requestKey) && !forceRefresh) {
      try {
        const result = await pendingFetches.get(requestKey);
        if (isMounted.current && result) {
          setFournisseurs(result.data);
          setPagination(result.pagination);
          setDataSource('cache (partagé)');
          if (setSource) setSource('cache (partagé)');
          setLoading(false);
          setRefreshing(false);
        }
        return;
      } catch {
        pendingFetches.delete(requestKey);
      }
    }

    // Limiter les tentatives pour éviter les boucles infinies
    if (retryCount > 5) {
      if (isMounted.current) {
        setLoading(false);
        setRefreshing(false);
        showToast('Erreur : trop de tentatives. Veuillez réessayer plus tard.', 'danger');
      }
      return;
    }

    // Définir l'état de chargement
    if (forceRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    const fetchPromise = (async () => {
      try {
        // 1. Cache (sauf forceRefresh)
        if (!forceRefresh) {
          const cached = await getCachedFournisseurs(cacheKey);
          if (cached && isMounted.current) {
            setFournisseurs(cached.data);
            setPagination(cached.pagination);
            setDataSource('cache');
            if (setSource) setSource('cache');
            setLoading(false);
            setRefreshing(false);
            return cached;
          }
        }

        // 2. Requête réseau
        const url = `${API_URL}?action=fetch&page=${page}&limit=${pagination.limit}&search=${encodeURIComponent(search)}`;
        const response = await fetch(url, { signal: controller.signal });

        // Gestion 429
        if (response.status === 429) {
          const stale = await getStaleFournisseurs(cacheKey);
          if (stale && isMounted.current) {
            setFournisseurs(stale.data);
            setPagination(stale.pagination);
            setDataSource('cache (périmé)');
            if (setSource) setSource('cache (périmé)');
            showToast('429 – Utilisation du cache périmé', 'warning');
            setLoading(false);
            setRefreshing(false);
            return stale;
          }
          // Sinon, backoff exponentiel
          const waitTime = Math.min(1000 * Math.pow(2, retryCount), 30000);
          await new Promise(resolve => setTimeout(resolve, waitTime));
          return fetchFournisseurs(page, search, forceRefresh, retryCount + 1);
        }

        if (!response.ok) {
          const stale = await getStaleFournisseurs(cacheKey);
          if (stale && isMounted.current) {
            setFournisseurs(stale.data);
            setPagination(stale.pagination);
            setDataSource('cache (périmé)');
            if (setSource) setSource('cache (périmé)');
            showToast(`Erreur réseau – Utilisation du cache`, 'warning');
            setLoading(false);
            setRefreshing(false);
            return stale;
          }
          throw new Error(`HTTP ${response.status}`);
        }

        const text = await response.text();
        let data;
        try {
          data = JSON.parse(text);
        } catch (parseError) {
          throw new Error(`Réponse JSON invalide : ${text.substring(0, 200)}`);
        }

        if (data.status === 'success') {
          const result = { data: data.data, pagination: data.pagination };
          if (isMounted.current) {
            setFournisseurs(data.data);
            setPagination(data.pagination);
            const source = forceRefresh ? 'live (forcé)' : 'live';
            setDataSource(source);
            if (setSource) setSource(source);
            await setCachedFournisseurs(cacheKey, result);
            if (forceRefresh) {
              showToast('Données actualisées', 'success');
            }
            setLoading(false);
            setRefreshing(false);
          }
          return result;
        } else {
          throw new Error(data.message || 'Erreur inconnue');
        }
      } catch (err) {
        if (err.name === 'AbortError') return null;
        // Dernier recours : cache périmé
        const stale = await getStaleFournisseurs(cacheKey);
        if (stale && isMounted.current) {
          setFournisseurs(stale.data);
          setPagination(stale.pagination);
          setDataSource('cache (périmé)');
          if (setSource) setSource('cache (périmé)');
          showToast(`Erreur – Utilisation du cache : ${err.message}`, 'warning');
          setLoading(false);
          setRefreshing(false);
          return stale;
        }
        if (isMounted.current) {
          showToast(err.message || 'Erreur de chargement', 'danger');
          setLoading(false);
          setRefreshing(false);
        }
        throw err;
      }
    })();

    pendingFetches.set(requestKey, fetchPromise);
    try {
      const result = await fetchPromise;
      return result;
    } finally {
      pendingFetches.delete(requestKey);
      if (abortControllerRef.current === controller) {
        abortControllerRef.current = null;
      }
    }
  }, [pagination.limit, showToast, setSource]);

  // ============================================================
  // RÉACTION AU REFRESH GLOBAL (via contexte)
  // ============================================================
  useEffect(() => {
    if (refreshKey && refreshKey > 0) {
      fetchFournisseurs(pagination.currentPage, searchTerm, true);
    }
  }, [refreshKey, fetchFournisseurs, pagination.currentPage, searchTerm]);

  // ============================================================
  // GESTION DE LA RECHERCHE (debounce)
  // ============================================================
  const handleSearch = (e) => {
    const val = e.target.value;
    setSearchTerm(val);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      fetchFournisseurs(1, val, false);
    }, 400);
  };

  // ============================================================
  // CRUD HANDLERS (avec invalidation du cache en rechargement forcé)
  // ============================================================
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const action = editingFournisseur ? 'update' : 'create';
    const method = editingFournisseur ? 'PUT' : 'POST';

    const payload = editingFournisseur
      ? { id: formData.id, nom: formData.nom, contact: formData.contact, telephone: formData.telephone, email: formData.email, type_services: formData.type_services }
      : { nom: formData.nom, contact: formData.contact, telephone: formData.telephone, email: formData.email, type_services: formData.type_services };

    try {
      const data = await fetchJSON(`${API_URL}?action=${action}`, {
        method: method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (data.status === 'success') {
        showToast(data.message, 'success');
        setShowModal(false);
        fetchFournisseurs(pagination.currentPage, searchTerm, true);
      } else {
        throw new Error(data.message || 'Erreur inconnue');
      }
    } catch (err) {
      showToast(err.message, 'danger');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!formData.id) {
      showToast('ID manquant', 'danger');
      return;
    }
    setDeleting(true);
    try {
      const data = await fetchJSON(`${API_URL}?action=delete&id=${formData.id}`, { method: 'DELETE' });
      if (data.status === 'success') {
        showToast(data.message, 'success');
        setShowDeleteModal(false);
        fetchFournisseurs(pagination.currentPage, searchTerm, true);
      } else {
        throw new Error(data.message || 'Erreur inconnue');
      }
    } catch (err) {
      showToast(err.message, 'danger');
    } finally {
      setDeleting(false);
    }
  };

  const handleImportCSV = async (e) => {
    e.preventDefault();
    if (!selectedFile) {
      showToast('Veuillez sélectionner un fichier CSV', 'danger');
      return;
    }
    setSubmitting(true);
    const formDataFile = new FormData();
    formDataFile.append('file', selectedFile);

    try {
      const data = await fetchJSON(`${API_URL}?action=import`, {
        method: 'POST',
        body: formDataFile
      });
      if (data.status === 'success') {
        showToast(data.message, 'success');
        setShowImportModal(false);
        setSelectedFile(null);
        fetchFournisseurs(1, searchTerm, true);
      } else {
        throw new Error(data.message || 'Erreur inconnue');
      }
    } catch (err) {
      showToast(err.message, 'danger');
    } finally {
      setSubmitting(false);
    }
  };

  const openEditModal = (item) => {
    setEditingFournisseur(item);
    setFormData({ ...item });
    setShowModal(true);
  };

  const openCreateModal = () => {
    setEditingFournisseur(null);
    setFormData({ id: '', nom: '', contact: '', telephone: '', email: '', type_services: '' });
    setShowModal(true);
  };

  // ============================================================
  // CHARGEMENT INITIAL ET NETTOYAGE
  // ============================================================
  useEffect(() => {
    isMounted.current = true;
    fetchFournisseurs();
    return () => {
      isMounted.current = false;
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      if (abortControllerRef.current) abortControllerRef.current.abort();
      pendingFetches.clear();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ============================================================
  // RENDU (avec styles inchangés, légère amélioration mobile)
  // ============================================================
  return (
    <>
      <style>{`
        /* ============================================================
           DARK BLUE GLASSMORPHISM – MANAGE FOURNISSEURS (amélioré)
           ============================================================ */
        :root {
          --accent-blue: #3b82f6;
          --accent-dark-blue: #1d4ed8;
          --accent-blue-dim: rgba(59, 130, 246, 0.10);
          --accent-blue-glow: rgba(59, 130, 246, 0.25);
          --bg-primary: #0b1428;
          --bg-gradient: radial-gradient(circle at 20% 30%, #132044, #0a0f1f);
          --card-bg: rgba(20, 40, 80, 0.55);
          --card-border: rgba(59, 130, 246, 0.15);
          --text-primary: #e0e7ff;
          --text-secondary: #94a3b8;
          --text-muted: #64748b;
          --radius: 16px;
          --shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
        }

        .monetra-page {
          background: var(--bg-gradient);
          min-height: 100vh;
          padding: clamp(0.75rem, 2vw, 2rem);
          color: var(--text-primary);
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
        }

        .glass-card {
          background: var(--card-bg);
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          border: 1px solid var(--card-border);
          border-radius: 24px;
          padding: clamp(1rem, 1.5vw, 1.8rem);
          box-shadow: var(--shadow);
          transition: all 0.2s ease;
          margin-bottom: 24px;
        }
        .glass-card:hover {
          border-color: rgba(59, 130, 246, 0.25);
          box-shadow: 0 12px 40px rgba(59, 130, 246, 0.05);
        }

        .badge-source {
          font-size: clamp(0.6rem, 0.9vw, 0.75rem);
          padding: 4px 12px;
          border-radius: 40px;
          background: rgba(59,130,246,0.15);
          color: var(--accent-blue);
          border: 1px solid rgba(59,130,246,0.12);
          white-space: nowrap;
          font-weight: 500;
        }
        .badge-source.live {
          background: rgba(46,204,113,0.15);
          color: #2ecc71;
          border-color: rgba(46,204,113,0.12);
        }
        .badge-source.warning {
          background: rgba(245,158,11,0.15);
          color: #f59e0b;
          border-color: rgba(245,158,11,0.12);
        }

        .monetra-btn {
          padding: 8px 16px;
          border-radius: 30px;
          border: 1px solid rgba(255,255,255,0.06);
          background: rgba(255,255,255,0.04);
          color: var(--text-primary);
          transition: all 0.25s ease;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-weight: 500;
          cursor: pointer;
          font-size: clamp(0.75rem, 1vw, 0.9rem);
          white-space: nowrap;
          min-height: 44px;
          touch-action: manipulation;
        }
        .monetra-btn:hover:not(:disabled) {
          background: rgba(255,255,255,0.08);
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(0,0,0,0.2);
        }
        .monetra-btn:disabled {
          opacity: 0.5;
          pointer-events: none;
        }
        .monetra-btn.primary {
          background: var(--accent-blue);
          color: #fff;
          border: none;
          box-shadow: 0 6px 20px var(--accent-blue-glow);
        }
        .monetra-btn.primary:hover:not(:disabled) {
          background: var(--accent-dark-blue);
          box-shadow: 0 8px 28px rgba(59, 130, 246, 0.4);
        }
        .monetra-btn.danger {
          border-color: #ef4444;
          color: #f87171;
        }
        .monetra-btn.danger:hover:not(:disabled) {
          background: rgba(239, 68, 68, 0.12);
        }
        .monetra-btn.outline-secondary {
          border-color: rgba(255,255,255,0.08);
          color: var(--text-secondary);
        }
        .monetra-btn.outline-secondary:hover:not(:disabled) {
          background: rgba(255,255,255,0.05);
        }
        .monetra-btn.p-2 {
          padding: 6px 10px;
          min-width: 36px;
          justify-content: center;
        }

        .search-wrapper {
          position: relative;
          display: flex;
          align-items: center;
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 30px;
          padding: 4px 4px 4px 16px;
          transition: all 0.3s ease;
          flex: 1;
          min-width: 180px;
        }
        .search-wrapper:focus-within {
          border-color: var(--accent-blue);
          box-shadow: 0 0 20px rgba(59, 130, 246, 0.08);
        }
        .search-wrapper input {
          background: transparent;
          border: none;
          color: var(--text-primary);
          outline: none;
          padding: 10px 0;
          flex: 1;
          font-size: clamp(0.85rem, 1.2vw, 1rem);
        }
        .search-wrapper input::placeholder {
          color: var(--text-muted);
        }

        .glass-table {
          width: 100%;
          border-collapse: collapse;
          font-size: clamp(0.75rem, 1vw, 0.9rem);
        }
        .glass-table th {
          padding: 12px 10px;
          text-align: left;
          font-size: clamp(0.6rem, 0.8vw, 0.75rem);
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: var(--accent-blue);
          border-bottom: 1px solid rgba(255,255,255,0.04);
          font-weight: 600;
        }
        .glass-table td {
          padding: 10px 10px;
          border-bottom: 1px solid rgba(255,255,255,0.02);
          vertical-align: middle;
        }
        .glass-table tr {
          transition: 0.2s;
        }
        .glass-table tr:hover {
          background: rgba(255,255,255,0.03);
        }

        .monetra-modal .modal-content {
          background: rgba(10, 20, 45, 0.92);
          backdrop-filter: blur(30px);
          -webkit-backdrop-filter: blur(30px);
          border: 1px solid rgba(59, 130, 246, 0.12);
          border-radius: 28px;
          color: var(--text-primary);
          box-shadow: 0 30px 80px rgba(0,0,0,0.8);
        }
        .monetra-modal .modal-header {
          border-bottom: 1px solid rgba(59, 130, 246, 0.06);
        }
        .monetra-modal .btn-close {
          filter: invert(1) brightness(200%);
          opacity: 0.5;
        }

        .monetra-input {
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 14px;
          color: var(--text-primary);
          padding: 12px;
          width: 100%;
          transition: 0.3s;
          font-size: clamp(0.85rem, 1.2vw, 1rem);
        }
        .monetra-input:focus {
          border-color: var(--accent-blue);
          outline: none;
          background: rgba(255,255,255,0.06);
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.06);
        }
        .monetra-input::placeholder {
          color: var(--text-muted);
        }

        .toast-monetra {
          background: rgba(10, 20, 45, 0.8) !important;
          backdrop-filter: blur(24px) !important;
          -webkit-backdrop-filter: blur(24px) !important;
          border: 1px solid rgba(59, 130, 246, 0.2) !important;
          border-radius: 16px !important;
          color: var(--text-primary) !important;
          box-shadow: 0 20px 50px rgba(0,0,0,0.6) !important;
        }

        .text-secondary { color: var(--text-secondary); }
        .text-muted { color: var(--text-muted); }
        .text-success { color: var(--accent-blue); }
        .text-danger { color: #ef4444; }
        .text-info { color: var(--accent-blue); }
        .fw-bold { font-weight: 600; }
        .small { font-size: 0.85rem; }
        .m-0 { margin: 0; }
        .mb-4 { margin-bottom: 1.5rem; }
        .me-2 { margin-right: 0.5rem; }
        .p-2 { padding: 0.5rem; }
        .p-3 { padding: 1rem; }
        .py-5 { padding-top: 3rem; padding-bottom: 3rem; }
        .gap-2 { gap: 0.5rem; }
        .gap-3 { gap: 1rem; }
        .d-flex { display: flex; }
        .flex-wrap { flex-wrap: wrap; }
        .align-items-center { align-items: center; }
        .justify-content-between { justify-content: space-between; }
        .text-end { text-align: right; }
        .text-center { text-align: center; }
        .overflow-hidden { overflow: hidden; }
        .table-responsive { overflow-x: auto; -webkit-overflow-scrolling: touch; }
        .border-top { border-top: 1px solid rgba(255,255,255,0.04); }
        .border-opacity-10 { opacity: 0.1; }

        /* ============================================================
           RESPONSIVE MOBILE
           ============================================================ */
        @media (max-width: 768px) {
          .monetra-page { padding: 12px; }
          .glass-card { padding: 1rem; }

          .d-flex.justify-content-between.align-items-center {
            flex-direction: column;
            align-items: stretch !important;
            gap: 12px;
          }
          .d-flex.justify-content-between.align-items-center > div:first-child {
            text-align: center;
          }
          .d-flex.gap-2 {
            flex-wrap: wrap;
            justify-content: center;
          }

          .search-wrapper {
            max-width: 100%;
            min-width: unset;
          }

          .glass-table th, .glass-table td {
            padding: 6px 8px;
            font-size: 0.7rem;
          }
          .glass-table th:last-child, .glass-table td:last-child {
            min-width: 80px;
          }

          .monetra-btn {
            font-size: 0.75rem;
            padding: 6px 12px;
            min-height: 38px;
            flex: 1 0 auto;
            justify-content: center;
          }
          .monetra-btn.p-2 {
            padding: 4px 8px;
            min-width: 32px;
          }

          .monetra-modal .modal-dialog {
            margin: 0.5rem;
          }
          .monetra-modal .modal-content {
            border-radius: 20px;
          }
          .monetra-modal .modal-footer {
            flex-direction: column-reverse;
          }
          .monetra-modal .modal-footer button {
            width: 100%;
            justify-content: center;
          }
        }
        @media (max-width: 480px) {
          .monetra-btn {
            font-size: 0.7rem;
            padding: 4px 10px;
            min-height: 34px;
          }
          .glass-table th, .glass-table td {
            font-size: 0.6rem;
            padding: 4px 6px;
          }
        }
      `}</style>

      <div className="monetra-page">
        {/* Header avec badge source */}
        <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
          <div>
            <h1 className="fw-bold mb-1" style={{ fontSize: 'clamp(1.4rem, 4vw, 2.2rem)' }}>
              Gestion des Fournisseurs
            </h1>
            <p className="text-secondary m-0" style={{ fontSize: 'clamp(0.75rem, 1.2vw, 0.95rem)' }}>
              Gérez vos fournisseurs, contacts et services.
            </p>
          </div>
          <div className="d-flex gap-2 flex-wrap align-items-center">
            <span className={`badge-source ${dataSource === 'live' ? 'live' : dataSource === 'cache (périmé)' ? 'warning' : ''}`}>
              {dataSource === 'cache' ? '📦 Cache' :
               dataSource === 'cache (périmé)' ? '⚠️ Cache périmé' :
               dataSource === 'cache (partagé)' ? '🔄 Partage' :
               dataSource === 'live' ? '🔄 Live' :
               dataSource === 'live (forcé)' ? '🔄 Forcé' : '📦 Cache'}
            </span>
            <button
              className="monetra-btn"
              onClick={() => fetchFournisseurs(pagination.currentPage, searchTerm, false)}
              disabled={loading || refreshing}
            >
              <FontAwesomeIcon icon={faSyncAlt} spin={loading || refreshing} /> Actualiser
            </button>
            <button
              className="monetra-btn primary"
              onClick={() => fetchFournisseurs(pagination.currentPage, searchTerm, true)}
              disabled={loading || refreshing}
            >
              <FontAwesomeIcon icon={faSyncAlt} spin={loading || refreshing} style={{ color: '#ef4444' }} /> Forcer
            </button>
            <button
              className="monetra-btn"
              onClick={() => setShowImportModal(true)}
              disabled={loading || refreshing}
            >
              <FontAwesomeIcon icon={faFileCsv} /> Importer CSV
            </button>
            <button
              className="monetra-btn primary"
              onClick={openCreateModal}
              disabled={loading || refreshing}
            >
              <FontAwesomeIcon icon={faPlus} /> Ajouter
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="glass-card d-flex justify-content-between align-items-center flex-wrap gap-3">
          <div className="search-wrapper" style={{ minWidth: '260px', flex: 1, maxWidth: '100%' }}>
            <FontAwesomeIcon icon={faSearch} className="text-secondary" />
            <input
              type="text"
              placeholder="Rechercher (nom, contact, email, service...)"
              value={searchTerm}
              onChange={handleSearch}
            />
          </div>
          <div className="text-secondary small">
            <span>{pagination.totalRecords} enregistrements</span>
          </div>
        </div>

        {/* Table Container */}
        <div className="glass-card p-0 overflow-hidden mt-4">
          <div className="table-responsive">
            <table className="glass-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Nom</th>
                  <th>Contact</th>
                  <th>Téléphone</th>
                  <th>Email</th>
                  <th>Type de services</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && !refreshing ? (
                  <tr><td colSpan="7" className="text-center py-5"><div className="spinner-border text-primary" role="status"></div></td></tr>
                ) : fournisseurs.length === 0 ? (
                  <tr><td colSpan="7" className="text-center py-5 text-secondary">Aucun fournisseur trouvé.</td></tr>
                ) : (
                  fournisseurs.map(item => (
                    <tr key={item.id}>
                      <td><span className="fw-bold text-secondary">#{item.id}</span></td>
                      <td><strong>{item.nom}</strong></td>
                      <td><span className="text-secondary">{item.contact}</span></td>
                      <td><span className="text-secondary">{item.telephone}</span></td>
                      <td><span className="text-secondary">{item.email}</span></td>
                      <td><span className="text-secondary">{item.type_services}</span></td>
                      <td className="text-end">
                        <button
                          className="monetra-btn p-2 me-1"
                          onClick={() => openEditModal(item)}
                          disabled={loading || refreshing}
                        >
                          <FontAwesomeIcon icon={faEdit} />
                        </button>
                        <button
                          className="monetra-btn danger p-2"
                          onClick={() => { setFormData(item); setShowDeleteModal(true); }}
                          disabled={loading || refreshing}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="p-3 border-top border-secondary border-opacity-10 d-flex justify-content-between align-items-center flex-wrap">
            <span className="text-secondary small">Page {pagination.currentPage} sur {pagination.totalPages}</span>
            <div className="d-flex gap-2">
              <button
                className="monetra-btn p-2"
                disabled={pagination.currentPage <= 1 || loading || refreshing}
                onClick={() => fetchFournisseurs(pagination.currentPage - 1, searchTerm, false)}
              >
                ‹
              </button>
              <button
                className="monetra-btn p-2"
                disabled={pagination.currentPage >= pagination.totalPages || loading || refreshing}
                onClick={() => fetchFournisseurs(pagination.currentPage + 1, searchTerm, false)}
              >
                ›
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* --- MODALES (inchangées) --- */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered className="monetra-modal">
        <Form onSubmit={handleFormSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>
              <FontAwesomeIcon icon={faEdit} className="text-success me-2" />
              {editingFournisseur ? 'Modifier' : 'Ajouter'} un fournisseur
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="row g-3">
              <div className="col-12">
                <label className="text-secondary small">Nom</label>
                <input
                  className="monetra-input"
                  name="nom"
                  value={formData.nom}
                  onChange={(e) => setFormData({...formData, nom: e.target.value})}
                  required
                  disabled={submitting}
                />
              </div>
              <div className="col-md-6">
                <label className="text-secondary small">Contact</label>
                <input
                  className="monetra-input"
                  name="contact"
                  value={formData.contact}
                  onChange={(e) => setFormData({...formData, contact: e.target.value})}
                  required
                  disabled={submitting}
                />
              </div>
              <div className="col-md-6">
                <label className="text-secondary small">Téléphone</label>
                <input
                  className="monetra-input"
                  name="telephone"
                  value={formData.telephone}
                  onChange={(e) => setFormData({...formData, telephone: e.target.value})}
                  required
                  disabled={submitting}
                />
              </div>
              <div className="col-md-6">
                <label className="text-secondary small">Email</label>
                <input
                  type="email"
                  className="monetra-input"
                  name="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                  disabled={submitting}
                />
              </div>
              <div className="col-md-6">
                <label className="text-secondary small">Type de services</label>
                <input
                  className="monetra-input"
                  name="type_services"
                  value={formData.type_services}
                  onChange={(e) => setFormData({...formData, type_services: e.target.value})}
                  required
                  disabled={submitting}
                />
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="outline-secondary"
              onClick={() => setShowModal(false)}
              className="monetra-btn outline-secondary"
              disabled={submitting}
            >
              Annuler
            </Button>
            <Button
              variant="success"
              type="submit"
              className="monetra-btn primary"
              disabled={submitting}
            >
              {submitting ? (
                <><FontAwesomeIcon icon={faSpinner} spin /> Enregistrement...</>
              ) : (
                'Enregistrer'
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* MODALE SUPPRESSION */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered className="monetra-modal">
        <Modal.Header closeButton>
          <Modal.Title className="text-danger">
            <FontAwesomeIcon icon={faExclamationTriangle} /> Confirmation
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Êtes-vous sûr de vouloir supprimer le fournisseur <strong>"{formData.nom}"</strong> ?
          <br /><span className="text-secondary small">Cette action est irréversible.</span>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => setShowDeleteModal(false)}
            className="monetra-btn outline-secondary"
            disabled={deleting}
          >
            Annuler
          </Button>
          <Button
            variant="danger"
            onClick={handleDelete}
            className="monetra-btn danger"
            disabled={deleting}
          >
            {deleting ? (
              <><FontAwesomeIcon icon={faSpinner} spin /> Suppression...</>
            ) : (
              'Confirmer la suppression'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* MODALE IMPORTATION CSV */}
      <Modal show={showImportModal} onHide={() => setShowImportModal(false)} centered className="monetra-modal">
        <Form onSubmit={handleImportCSV}>
          <Modal.Header closeButton>
            <Modal.Title>
              <FontAwesomeIcon icon={faFileCsv} className="text-info me-2" />
              Importer des données CSV
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="text-secondary small">Le fichier doit avoir les colonnes dans cet ordre :<br /> 
              <code>nom, contact, telephone, email, type_services</code>
            </p>
            <input
              type="file"
              accept=".csv"
              className="monetra-input"
              onChange={(e) => setSelectedFile(e.target.files[0])}
              required
              disabled={submitting}
            />
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="outline-secondary"
              onClick={() => setShowImportModal(false)}
              className="monetra-btn outline-secondary"
              disabled={submitting}
            >
              Annuler
            </Button>
            <Button
              variant="success"
              type="submit"
              className="monetra-btn primary"
              disabled={submitting}
            >
              {submitting ? (
                <><FontAwesomeIcon icon={faSpinner} spin /> Importation...</>
              ) : (
                'Importer'
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* TOAST */}
      {toast.show && (
        <div style={{ position: 'fixed', top: '20px', right: '20px', zIndex: 9999 }}>
          <div className={`toast-monetra p-3 ${toast.variant}`} style={{ minWidth: 'clamp(200px, 30vw, 300px)' }}>
            <div className="d-flex align-items-center gap-3">
              <FontAwesomeIcon
                icon={toast.variant === 'success' ? faCheckCircle : faExclamationTriangle}
                className={toast.variant === 'success' ? 'text-success' : 'text-danger'}
              />
              <span style={{ fontSize: 'clamp(0.85rem, 1.2vw, 1rem)' }}>{toast.message}</span>
              <button
                className="btn-close-custom ms-auto"
                onClick={() => setToast({ ...toast, show: false })}
                style={{ background: 'transparent', border: 'none', color: 'var(--text-primary)', opacity: 0.5, fontSize: '1.2rem' }}
              >
                ✕
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ManageFournisseurs;